import type { QuestTemplateFirestore, QuestType } from '../services/questTypes';

interface QuestTemplatesStatisticsProps {
    allQuestTemplates: QuestTemplateFirestore[];
}

export const QuestTemplatesStatistics = ({ allQuestTemplates }: QuestTemplatesStatisticsProps) => {
    // Calculate statistics
    const totalTemplates = allQuestTemplates.length;

    const publishedCount = allQuestTemplates.filter(template => template.isPublished).length;
    const unpublishedCount = totalTemplates - publishedCount;

    const freeCount = allQuestTemplates.filter(template => template.isFree).length;
    const premiumCount = totalTemplates - freeCount;

    const requiredCount = allQuestTemplates.filter(template => !template.isOptional).length;
    const optionalCount = allQuestTemplates.filter(template => template.isOptional).length;

    const primaryCount = allQuestTemplates.filter(template => template.isPrimary).length;
    const secondaryCount = totalTemplates - primaryCount;

    // Count by type
    const typeCount: Record<QuestType, number> = {
        'chat': 0,
        'check-in': 0,
        'story': 0,
        'lesson': 0,
        'community-question': 0,
        'tool': 0,
        'feedback': 0,
        'rate': 0,
    };

    allQuestTemplates.forEach(template => {
        typeCount[template.type] = (typeCount[template.type] || 0) + 1;
    });

    // Get the sorted types with counts (non-zero only)
    const typesWithCounts = Object.entries(typeCount)
        .filter(([_, count]) => count > 0)
        .sort((a, b) => b[1] - a[1]);

    const getTypeLabel = (type: string): string => {
        const labels: Record<string, string> = {
            'chat': 'Chat',
            'check-in': 'Check-in',
            'story': 'Story',
            'lesson': 'Lesson',
            'community-question': 'Community',
            'tool': 'Tool',
            'feedback': 'Feedback',
            'rate': 'Rate'
        };
        return labels[type] || type;
    };

    return (
        <div className="bg-white rounded-lg shadow-md p-3 mb-4">
            <h3 className="text-sm font-semibold text-gray-800 mb-2">Quest Templates Overview</h3>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-2">
                {/* Total Templates */}
                <div className="bg-blue-50 rounded p-2">
                    <div className="text-xs text-gray-600">Total</div>
                    <div className="font-bold text-blue-600">{totalTemplates}</div>
                </div>

                {/* Published Status */}
                <div className="bg-green-50 rounded p-2">
                    <div className="text-xs text-gray-600">Published / Unpublished</div>
                    <div className="text-sm font-bold text-green-600">
                        {publishedCount} <span className="text-gray-400">/</span> <span className="text-red-600">{unpublishedCount}</span>
                    </div>
                </div>

                {/* Free vs Premium */}
                <div className="bg-purple-50 rounded p-2">
                    <div className="text-xs text-gray-600">Free / Premium</div>
                    <div className="text-sm font-bold text-purple-600">
                        {freeCount} <span className="text-gray-400">/</span> <span className="text-purple-700">{premiumCount}</span>
                    </div>
                </div>

                {/* Required vs Optional */}
                <div className="bg-orange-50 rounded p-2">
                    <div className="text-xs text-gray-600">Required / Optional</div>
                    <div className="text-sm font-bold text-orange-600">
                        {requiredCount} <span className="text-gray-400">/</span> <span className="text-orange-400">{optionalCount}</span>
                    </div>
                </div>

                {/* Primary vs Secondary */}
                <div className="bg-indigo-50 rounded p-2">
                    <div className="text-xs text-gray-600">Primary / Secondary</div>
                    <div className="text-sm font-bold text-indigo-600">
                        {primaryCount} <span className="text-gray-400">/</span> <span className="text-indigo-400">{secondaryCount}</span>
                    </div>
                </div>
            </div>

            {/* Quest Types Breakdown */}
            <div className="bg-gray-50 rounded p-2">
                <div className="flex flex-wrap gap-1">
                    {typesWithCounts.map(([type, count]) => (
                        <span
                            key={type}
                            className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-700"
                        >
                            {getTypeLabel(type)}: {count}
                        </span>
                    ))}
                </div>
            </div>
        </div>
    );
};
